import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import { ethers } from "ethers";

const WalletContext = createContext(null);

export function WalletProvider({ children }) {
    const [state, setState] = useState({
        account: null,
        walletName: null,
        connected: false,
        provider: null,
        signer: null,
        chainId: null,
    });

    const detectWalletName = (eth) => {
        if (!eth) return "None";
        if (eth.isMetaMask) return "MetaMask";
        if (eth.isTrust || eth.isTrustWallet) return "Trust Wallet";
        return "Injected";
    };

    const hydrateFromProvider = useCallback(async (prov) => {
        const signer = await prov.getSigner();
        const addr = await signer.getAddress();
        const net = await prov.getNetwork();
        setState({
            account: addr,
            walletName: detectWalletName(window.ethereum),
            connected: true,
            provider: prov,
            signer,
            chainId: Number(net.chainId),
        });
    }, []);

    const connect = useCallback(async () => {
        const eth = window.ethereum;
        if (!eth) {
            alert("No wallet detected. Install MetaMask or Trust Wallet, or use their in-app browser.");
            return;
        }
        const prov = new ethers.BrowserProvider(eth);
        await prov.send("eth_requestAccounts", []);
        await hydrateFromProvider(prov);
    }, [hydrateFromProvider]);

    const disconnect = useCallback(() => {
        // We can’t programmatically disconnect injected wallets; just clear local state.
        setState({
            account: null,
            walletName: null,
            connected: false,
            provider: null,
            signer: null,
            chainId: null,
        });
    }, []);

    // React to wallet events (accounts/chain changed)
    useEffect(() => {
        const eth = window.ethereum;
        if (!eth) return;

        const onAccountsChanged = async (accounts) => {
            if (accounts && accounts[0]) {
                const prov = new ethers.BrowserProvider(eth);
                await hydrateFromProvider(prov);
            } else {
                disconnect();
            }
        };

        const onChainChanged = async () => {
            if (!state.provider) return;
            await hydrateFromProvider(new ethers.BrowserProvider(eth));
        };

        eth.on?.("accountsChanged", onAccountsChanged);
        eth.on?.("chainChanged", onChainChanged);
        return () => {
            eth.removeListener?.("accountsChanged", onAccountsChanged);
            eth.removeListener?.("chainChanged", onChainChanged);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.provider, hydrateFromProvider, disconnect]);

    const value = useMemo(
        () => ({ ...state, connect, disconnect }),
        [state, connect, disconnect]
    );

    return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
}

export function useWallet() {
    const ctx = useContext(WalletContext);
    if (!ctx) throw new Error("useWallet must be used within <WalletProvider>");
    return ctx;
}
